import "toastify-js/src/toastify.css";
import DOMPurify from "dompurify";
import ScrollReveal from "scrollreveal";
import Toastify from "toastify-js";
import moment from "moment";
import { WhiteListedURLs } from "../static";
import { domSanitizeOptions } from "../domSanitizeOptions";
import CustomWebHaptics from "../utils/webHaptics";
export const TOAST_CONFIG = {
    duration: 2500,
    newWindow: true,
    close: true,
    avatar: "../assets/images/static/webp/logo_24x24.webp",
    gravity: "bottom",
    position: "center",
    stopOnFocus: true,
    style: {
        background: "#0f3d75",
        borderRadius: "24px",
        text: "center"
    },
};
export class DarkLightMode {
    static singleton = null;
    mediaQuery;
    currentTheme = "auto";
    iconElement = null;
    darkLightButton = null;
    mediaQueryListener = null;
    darkLightClickHandler = null;
    constructor() {
        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.initialize();
    }
    static getInstance() {
        if (!DarkLightMode.singleton) {
            DarkLightMode.singleton = new DarkLightMode();
        }
        return DarkLightMode.singleton;
    }
    static destroyInstance() {
        if (DarkLightMode.singleton) {
            DarkLightMode.singleton.destroy();
            DarkLightMode.singleton = null;
        }
    }
    initialize() {
        this.loadThemeFromStorage();
        this.applyTheme();
        this.mediaQueryListener = this.handleSystemThemeChange.bind(this);
        this.mediaQuery.addEventListener("change", this.mediaQueryListener);
    }
    handleSystemThemeChange() {
        if (this.currentTheme === "auto") {
            this.applyTheme();
            this.updateIcon();
        }
    }
    loadThemeFromStorage() {
        try {
            const savedTheme = localStorage.getItem("userThemePreference");
            const options = ["dark", "light", "auto"];
            if (savedTheme && options.includes(savedTheme)) {
                this.currentTheme = savedTheme;
            }
        }
        catch (error) {
            console.error("Could not load theme from localStorage.", error);
        }
    }
    saveThemeToStorage(val) {
        try {
            localStorage.setItem("userThemePreference", val);
        }
        catch (error) {
            console.error("Could not save theme to localStorage", error);
        }
    }
    notifyUserAboutThemeChange() {
        Toastify({
            ...TOAST_CONFIG,
            text: `Saved your theme preference.`,
            close: false,
            duration: 2500,
            ariaLive: "polite"
        }).showToast();
    }
    applyTheme() {
        const effectiveTheme = this.getEffectiveTheme();
        document.documentElement.setAttribute("data-bs-theme", effectiveTheme);
        document.documentElement.setAttribute("data-theme", effectiveTheme);
    }
    getEffectiveTheme() {
        if (this.currentTheme === "auto") {
            return this.mediaQuery.matches ? "dark" : "light";
        }
        return this.currentTheme;
    }
    updateIcon() {
        if (!this.iconElement) {
            return;
        }
        const effectiveTheme = this.getEffectiveTheme();
        if (effectiveTheme === "dark") {
            this.iconElement.classList.remove("bi-sun");
            this.iconElement.classList.add("bi-moon-stars");
        }
        else {
            this.iconElement.classList.remove("bi-moon-stars");
            this.iconElement.classList.add("bi-sun");
        }
    }
    dayNightModeSwitching(elem, elemToBeManip) {
        this.iconElement = document.querySelector(`${elemToBeManip}`);
        this.updateIcon();
        if (!elem) {
            console.error("Cannot find element:" + elem);
            return;
        }
        if (this.darkLightButton && this.darkLightClickHandler) {
            this.darkLightButton.removeEventListener("click", this.darkLightClickHandler);
        }
        this.darkLightButton = elem;
        this.darkLightClickHandler = () => {
            const currentEffectiveTheme = this.getEffectiveTheme();
            this.currentTheme = currentEffectiveTheme === "dark" ? "light" : "dark";
            this.notifyUserAboutThemeChange();
            this.saveThemeToStorage(this.currentTheme);
            this.applyTheme();
            this.updateIcon();
        };
        elem.addEventListener("click", this.darkLightClickHandler);
    }
    destroy() {
        if (this.mediaQueryListener) {
            this.mediaQuery.removeEventListener("change", this.mediaQueryListener);
            this.mediaQueryListener = null;
        }
        if (this.darkLightButton && this.darkLightClickHandler) {
            this.darkLightButton.removeEventListener("click", this.darkLightClickHandler);
            this.darkLightClickHandler = null;
            this.darkLightButton = null;
        }
        this.iconElement = null;
    }
}
export class Template {
    nonce;
    constructor() { this.nonce = generateNonce(); }
    ;
    createTemplate(content, target) {
        if (typeof content !== "string" || !content.trim()) {
            throw new Error("Template content must be a non-empty string");
        }
        const template = document.createElement("template");
        const sanitizedContent = DOMPurify.sanitize(content, domSanitizeOptions);
        template.innerHTML = sanitizedContent;
        try {
            if (!content || !target) {
                throw new Error("Content or target does not exist.");
            }
            const fragment = template.content.cloneNode(true);
            if (this.nonce) {
                fragment.querySelectorAll("style, script").forEach((element) => {
                    element.setAttribute("nonce", this.nonce);
                });
            }
            target.appendChild(fragment);
            return template;
        }
        catch (error) {
            throw new Error("Unable to append template content as a web component: " + error);
        }
    }
}
export class HorizontalMiddleMouseScroll {
    hmmsScroll(element) {
        const elem = document.querySelector(element);
        if (!elem)
            return () => { };
        let isScrolling = false;
        let targetScrollLeft = elem.scrollLeft;
        const handleScroll = (e) => {
            e.preventDefault();
            const scrollAmount = e.deltaY < 0 ? -35 : 35;
            targetScrollLeft += scrollAmount;
            if (!isScrolling) {
                isScrolling = true;
                requestAnimationFrame(() => {
                    elem.scrollTo({
                        left: targetScrollLeft,
                        behavior: "smooth"
                    });
                    isScrolling = false;
                });
            }
        };
        const updateScrollBehavior = () => {
            const isMobileView = window.innerWidth <= 1024;
            elem.removeEventListener("wheel", handleScroll);
            if (isMobileView) {
                elem.addEventListener("wheel", handleScroll);
            }
        };
        updateScrollBehavior();
        window.addEventListener("resize", updateScrollBehavior);
        return () => {
            elem.removeEventListener("wheel", handleScroll);
            window.removeEventListener("resize", updateScrollBehavior);
        };
    }
}
export class DomEvents {
    pendingTimeouts = new Set();
    async appendContent(target, content) {
        if (!target && !content) {
            console.error("Please provide DOM properties to be generated.");
            return;
        }
        try {
            for (const i in content) {
                const p = document.createElement("p");
                p.textContent = content[i];
                p.className = "motto-element p-3 fs-6 fw-bolder rounded-5 pe-none shadow-sm mb-0";
                await new Promise((resolve) => {
                    const id = setTimeout(() => {
                        this.pendingTimeouts.delete(id);
                        if (document.contains(target)) {
                            target.appendChild(p);
                        }
                        resolve();
                    }, 1000);
                    this.pendingTimeouts.add(id);
                });
                if (this.pendingTimeouts.size === 0 && !document.contains(target)) {
                    break;
                }
            }
        }
        catch (error) {
            throw new Error(`Error while generating motto elements: ${error}`);
        }
    }
    destroy() {
        this.pendingTimeouts.forEach((id) => clearTimeout(id));
        this.pendingTimeouts.clear();
    }
}
export class TypeWriterDisplay {
    targetElement;
    heroParts;
    textIndex = 0;
    charIndex = 0;
    isDeleting = false;
    timeoutId = null;
    destroyed = false;
    constructor(heroParts, elementId) {
        this.heroParts = heroParts;
        const targetElement = document.getElementById(elementId);
        if (targetElement) {
            this.targetElement = targetElement;
        }
        else {
            throw new Error(`Target element not found`);
        }
        this.targetElement = targetElement;
        this.type();
    }
    type() {
        if (this.destroyed)
            return;
        const currentText = this.heroParts.occupationsData[this.textIndex];
        if (!currentText) {
            console.error("Please provide text for typing effect.");
            return;
        }
        const displayedText = currentText.substring(0, this.charIndex);
        const textSpan = this.targetElement.querySelector(".ocps-written-text");
        if (textSpan) {
            textSpan.textContent = displayedText;
        }
        let typingDelay = 100;
        if (!this.isDeleting) {
            if (this.charIndex < currentText.length) {
                this.charIndex++;
            }
            else {
                typingDelay = 2000;
                this.isDeleting = true;
            }
        }
        else {
            if (this.charIndex > 0) {
                this.charIndex--;
                typingDelay = 50;
            }
            else {
                this.isDeleting = false;
                this.textIndex = (this.textIndex + 1) % this.heroParts.occupationsData.length;
                typingDelay = 500;
            }
        }
        this.timeoutId = setTimeout(() => {
            this.type();
        }, typingDelay);
    }
    destroy() {
        this.destroyed = true;
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
}
export class PromoFunctions {
    createPromoContent(target, posts, renderFunction) {
        const container = document.querySelector(`${target}`);
        if (container) {
            const cardsHTML = posts
                .map((post) => renderFunction(post))
                .join("\n");
            const cleaned = DOMPurify.sanitize(cardsHTML);
            container.innerHTML = cleaned;
        }
    }
    createVerticalTabContent(target, data) {
        const container = document.getElementById("tabGroupDetailsLister");
        if (!container || !data[target]) {
            return;
        }
        else {
            const { iconData, desc } = data[target];
            container.innerHTML = "";
            const wrapper = document.createElement("section");
            const icon = document.createElement("i");
            wrapper.className = "promo-tab-content h-100 d-flex flex-column align-items-center justify-content-evenly";
            icon.className = `promo-tc-icon ${iconData}`;
            const p = document.createElement("p");
            p.className = "text-center text-break fs-4 w-100";
            p.textContent = desc;
            wrapper.appendChild(icon);
            wrapper.appendChild(p);
            container.appendChild(wrapper);
        }
    }
    bindVerticalTabEventsAndautoCycleTabs(data) {
        const buttons = Array.from(document.querySelectorAll(".promo-desc-tab-group-btn"));
        if (!buttons.length) {
            return () => { };
        }
        let index = 0;
        const duration = 5000;
        let activeInterval = null;
        const clickHandlers = [];
        const isMobile = () => window.innerWidth <= 768;
        const isElementInViewport = (el) => {
            const rect = el.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            return rect.top < windowHeight && rect.bottom > 0;
        };
        const scrollToActiveTab = (activeButton) => {
            if (!isMobile()) {
                return;
            }
            if (!isElementInViewport(activeButton)) {
                return;
            }
            activeButton.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center"
            });
        };
        const clearAllProgressBars = () => {
            buttons.forEach(btn => {
                const bar = btn.querySelector(".progress-bar");
                if (bar) {
                    bar.style.width = "0%";
                    bar.classList.remove("progress-bar-animated");
                }
            });
            if (activeInterval) {
                clearInterval(activeInterval);
                activeInterval = null;
            }
        };
        const startProgressForButton = (button) => {
            clearAllProgressBars();
            const progressBar = button.querySelector(".progress-bar");
            if (!progressBar)
                return;
            progressBar.style.width = "0%";
            progressBar.classList.add("progress-bar-animated");
            let progress = 0;
            const step = 100 / (duration / 100);
            activeInterval = setInterval(() => {
                progress += step;
                progressBar.style.width = `${progress}%`;
                if (progress >= 100) {
                    clearInterval(activeInterval);
                    activeInterval = null;
                    index = (index + 1) % buttons.length;
                    const nextButton = buttons[index];
                    const type = nextButton.getAttribute("data-type");
                    if (type) {
                        this.createVerticalTabContent(type, data);
                        scrollToActiveTab(nextButton);
                    }
                    startProgressForButton(nextButton);
                }
            }, 100);
        };
        buttons.forEach((btn, i) => {
            const handler = () => {
                index = i;
                const type = btn.getAttribute("data-type");
                if (type) {
                    this.createVerticalTabContent(type, data);
                    startProgressForButton(btn);
                    scrollToActiveTab(btn);
                }
            };
            btn.addEventListener("click", handler);
            clickHandlers.push({ btn, handler });
        });
        if (buttons.length > 0) {
            const firstButton = buttons[index];
            if (!firstButton)
                return () => { };
            const initialType = firstButton.getAttribute("data-type");
            if (initialType) {
                this.createVerticalTabContent(initialType, data);
                scrollToActiveTab(firstButton);
                startProgressForButton(firstButton);
            }
        }
        return () => {
            if (activeInterval) {
                clearInterval(activeInterval);
                activeInterval = null;
            }
            clickHandlers.forEach(({ btn, handler }) => {
                btn.removeEventListener("click", handler);
            });
            clearAllProgressBars();
        };
    }
}
export class ScrollRevealAction {
    scrollReveal(classes) {
        for (const id of classes) {
            ScrollReveal().reveal(`.${id}`), { delay: 15000 };
        }
    }
}
export function formatDate(date) {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}
;
export function generateNonce() {
    try {
        const scriptWithNonce = document.querySelector("script[nonce]");
        if (scriptWithNonce) {
            return scriptWithNonce.getAttribute("nonce") || "";
        }
        const styleWithNonce = document.querySelector("style[nonce]");
        if (styleWithNonce) {
            return styleWithNonce.getAttribute("nonce") || "";
        }
        console.warn("No nonce found. CSP may block dynamic content.");
        return "";
    }
    catch (error) {
        console.error("Error retrieving nonce:", error);
        return "";
    }
}
export function insertApprovedScript({ scriptItself, target, attributes = {} }) {
    if (!target) {
        throw new Error(`Unable to insert script. ${target} element does not exist.`);
    }
    if (!WhiteListedURLs.approvedScripts.has(scriptItself)) {
        throw new Error(`Script source ${scriptItself} is not approved. Aborting procedure...`);
    }
    try {
        const element = document.createElement("script");
        element.src = scriptItself;
        element.defer = attributes.defer ?? false;
        element.async = attributes.async ?? false;
        const nonceGenerated = generateNonce();
        element.nonce = nonceGenerated;
        target.appendChild(element);
        return element;
    }
    catch (error) {
        throw new Error(`Failed to create script ${scriptItself}: ${error}`);
    }
}
export function insertToastifiedMessage(message) {
    try {
        const msg = message;
        if (msg) {
            Toastify({
                ...TOAST_CONFIG,
                text: msg,
                close: false,
                duration: 2500,
                ariaLive: "polite"
            }).showToast();
        }
    }
    catch (error) {
        throw new Error("Unable to insert toastify message" + error);
    }
}
export function addBackgroundBasedOnVerticalScroll(mainElement, target, className) {
    const mainHtmlElement = document.getElementById(mainElement);
    const targetHtmlElement = document.getElementById(target);
    if (!mainHtmlElement && !targetHtmlElement) {
        console.warn(`Elements with id "${mainElement} and ${target}" not found`);
        return () => { };
    }
    const handleScroll = () => {
        if (mainHtmlElement.scrollTop > 15) {
            targetHtmlElement.classList.add(className);
            ;
        }
        else if (mainHtmlElement.scrollTop <= 15) {
            targetHtmlElement.classList.remove(className);
        }
    };
    mainHtmlElement.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => mainHtmlElement.removeEventListener("scroll", handleScroll);
}
export function colorfulBannerName(array, targetElement) {
    if (!array) {
        console.error("Please provide an array.");
        return;
    }
    const colors = [
        "#70369D",
        "#4B369D",
        "#487DE7",
        "#79C314",
        "#FFFF00",
        "#FEA500",
        "#E91416"
    ];
    const shuffledColors = [...colors].sort(() => Math.random() - 0.7);
    const targetElem = document.querySelector(`#${targetElement}`);
    let colorIndex = 0;
    array.forEach((key) => {
        if (targetElem && key) {
            const eachLetter = document.createElement("span");
            eachLetter.textContent = key;
            eachLetter.style.color = shuffledColors[colorIndex];
            colorIndex++;
            targetElem.appendChild(eachLetter);
        }
        else {
            console.error("Target element or array missing for banner.");
            return;
        }
    });
}
export function applyHapticsToModals() {
    const modalElements = [
        ...document.querySelectorAll("component-custom-button[data-modal]"),
        ...document.querySelectorAll("button[data-modal]")
    ];
    const haptics = CustomWebHaptics.getInstance();
    const cleanups = [];
    modalElements.forEach(element => {
        const handler = (e) => {
            if (e.target) {
                haptics.triggerHaptic("success");
            }
        };
        element.addEventListener("click", handler);
        cleanups.push(() => element.removeEventListener("click", handler));
    });
    return () => {
        cleanups.forEach((fn) => fn());
    };
}
export function normalizeDateToDayMonthYear(input) {
    if (!input)
        return "Please provide date";
    const format = "DD-MM-YYYY";
    return moment(input).format(format);
}
